import { FormsEntities } from '@bimser/common';
import { IPropertyField } from '../../../entities/common';
import { JSX } from 'react';
import { IUserEvent } from '../../../entities/services';
import {
	IControlExecutedServerEvent,
	IPropertyChangedEventArgs,
	IPropertyChangingEventArgs
} from '../../runtime/entities/EventArgs';
import { IInputControlProperties } from '../entities/IInputControlProperties';
import DataBoundControl from './DataBoundControl';
import { IDictionary } from '@bimser/core';
import _isArray from 'lodash/isArray';
import _isEqual from 'lodash/isEqual';

export default class InputControl<TValue> extends DataBoundControl {
	_value: TValue;
	tabIndex: number;

	get value(): TValue {
		return this._value;
	}

	set value(_value: TValue) {
		this._value = _value;
	}

	constructor() {
		super();

		this.__subscribeValueChangingEvent();
	}

	__subscribeValueChangingEvent() {
		this.subscribe(
			'system:PropertyChanging:value',
			(eventArgs: FormsEntities.IPropertyChangeEventArgs) =>
				new Promise<void>((resolve, reject) => {
					this.__valueChangingEvent(eventArgs, resolve, reject);
				})
		);
	}

	__valueChangingEvent(
		eventArgs: FormsEntities.IPropertyChangeEventArgs,
		resolve: (value: void | PromiseLike<void>) => void,
		reject: (reason?: any) => void,
		callback?: () => void
	) {
		if (eventArgs.value !== this.value) {
			const valueArgument = {
				name: this.name,
				newValue: eventArgs.value,
				oldValue: this.value,
				cancel: false
			};

			// TRIGGER VALUE CHANGING CLIENT EVENT
			this.emit<IPropertyChangingEventArgs<TValue>>('event:OnValueChanging', valueArgument)
				.then(() => {
					if (!valueArgument.cancel) {
						// TRIGGER VALUE CHANGING SERVER EVENT
						this.emit<IUserEvent<IPropertyChangedEventArgs<TValue>>>(
							'event:server:OnValueChanging',
							{
								argument: valueArgument,
								controlId: this.name,
								name: 'ValueChanging',
								sender: 'web'
							}
						)
							.then(args => {
								const executedServerEvent: IControlExecutedServerEvent =
									args[0]?.executedServerEvent;
								if (
									!executedServerEvent ||
									!executedServerEvent?.parameters.cancel
								) {
									if (callback) {
										callback();
									} else {
										resolve();
									}
								} else {
									reject();
								}
							})
							.catch(() => {
								reject();
							});
					} else {
						reject();
					}
				})
				.catch(() => {
					reject();
				});
		} else {
			reject();
		}
	}

	getValue() {
		return this.value;
	}

	setValue(value: TValue) {
		return this.set('value', value);
	}

	__loadProperties(data: IInputControlProperties<TValue>) {
		super.__loadProperties(data);

		this.__proxyChanged.tabIndex = data.tabIndex;

		this.__beforeLoadValueProperty(data);
	}

	__beforeLoadValueProperty(data: IInputControlProperties<TValue>) {
		if (_isArray(data.value)) {
			if (!_isEqual(this.value, data.value)) {
				this.__loadValueProperty(data.value);
			}
		} else {
			const booelanValueResetFlag =
				this.__form.displayMode === 'preview' &&
				typeof this.value === 'boolean' &&
				typeof data.value === 'undefined';
			if (!booelanValueResetFlag && this.value !== data.value) {
				this.__loadValueProperty(data.value);
			}
		}
	}

	__loadValueProperty(value: TValue) {
		const oldValue = this.value;
		this.__proxyChanged.value = value;
		if (this.__isInitialized) {
			const argument = {
				name: this.name,
				newValue: value,
				oldValue: oldValue
			};

			this.emit<IPropertyChangedEventArgs<TValue>>('event:OnValueChanged', argument);

			this.emit<IUserEvent<IPropertyChangedEventArgs<TValue>>>(
				'event:server:OnValueChanged',
				{
					argument,
					controlId: this.name,
					name: 'ValueChanged',
					sender: 'web'
				}
			);
		}
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'tabIndex',
				type: 'number'
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			value: this.value,
			tabIndex: this.tabIndex
		};
	}

	__render(children?: React.ReactNode): JSX.Element {
		return super.__render(children);
	}
}
